import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as actions from './DefaultLocations.ducks';

import Loading from 'zignal-landscape/lib/Loading';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import { Light } from 'themes';
import Heading from 'zignal-landscape/lib/Heading';
import Select from 'zignal-landscape/lib/Select';
import Button from 'zignal-landscape/lib/Button';
import WarningIcon from 'zignal-landscape/lib/Icon/WarningIcon';
import themeable from 'zignal-landscape/lib/themeable';

import DefaultLocationsTable from './components/DefaultLocationsTable';
import DefaultLocationsTableFooter from './components/DefaultLocationsTableFooter';
import CreateDefaultLocationModal from './components/CreateDefaultLocationModal';
import EditDefaultLocationModal from './components/EditDefaultLocationModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import getPlatformOptions from './platformOptions';

// box-sizing makes landscape Select grow when an option is selected.
// Override singleValue with padding/lineHeight/color overrides.
const selectStyles = {
  singleValue: (base) => ({...base, padding: 0, lineHeight: 'inherit', color: 'inherit'}),
};

const mapStateToProps = (state) => ({
  locations: state.DefaultLocations.locations,
  total: state.DefaultLocations.total,
  isFetching: state.DefaultLocations.isFetching,
  error: state.DefaultLocations.error,
  platform: state.DefaultLocations.platform,
  currentPage: state.DefaultLocations.currentPage,
  pageSize: state.DefaultLocations.pageSize,
  isCreating: state.DefaultLocations.isCreating,
  createError: state.DefaultLocations.createError,
  isPatching: state.DefaultLocations.isPatching,
  patchError: state.DefaultLocations.patchError,
  isDeleting: state.DefaultLocations.isDeleting,
  deleteError: state.DefaultLocations.deleteError,
  isCreateModalOpen: state.DefaultLocations.showCreateModal,
  isEditModalOpen: state.DefaultLocations.showEditModal,
  isDeleteModalOpen: state.DefaultLocations.showDeleteModal,
  selectedLocation: state.DefaultLocations.selectedLocation,
  lookupResults: state.DefaultLocations.lookupResults,
  isLookingUp: state.DefaultLocations.isLookingUp,
  lookupError: state.DefaultLocations.lookupError,
  isInternalAdmin: state.AppUser.isInternalAdmin,
  user: state.AppUser.user,
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const DefaultLocations = ({
  styles,
  locations,
  total,
  isFetching,
  error,
  platform,
  currentPage,
  pageSize,
  isCreating,
  createError,
  isPatching,
  patchError,
  isDeleting,
  deleteError,
  isCreateModalOpen,
  isEditModalOpen,
  isDeleteModalOpen,
  selectedLocation,
  lookupResults,
  isLookingUp,
  lookupError,
  isInternalAdmin,
  user,
  fetchDefaults,
  setPlatformFilter,
  setPage,
  setPageSize,
  createDefault,
  patchDefault,
  deleteDefault,
  lookupLocationForward,
  lookupLocationReverse,
  clearLookup,
  showCreateModal,
  hideCreateModal,
  showEditModal,
  hideEditModal,
  showDeleteModal,
  hideDeleteModal,
  clearState,
}) => {
  const hasFullAccountAccess = user && user.fullAccountAccessEnabled === true;
  const hasAccess = isInternalAdmin && hasFullAccountAccess;

  // Platform changes refetch via setPlatformFilterSaga; this effect handles
  // the initial fetch and the AppUser hydration flip (hasAccess false→true).
  React.useEffect(() => {
    if (hasAccess && platform) {
      fetchDefaults();
    }
  }, [hasAccess]);

  React.useEffect(() => {
    return () => {
      clearState();
    };
  }, []);

  const platformOptions = React.useMemo(() => getPlatformOptions(), []);

  const handlePlatformChange = (option) => {
    const value = option && option.value ? option.value : option;
    setPlatformFilter(value || '');
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (option) => {
    const value = option && option.value ? option.value : option;
    setPageSize(value);
  };

  const handleCreateSubmit = (data, options) => {
    createDefault(data, options);
  };

  const handleEditSubmit = (id, updates) => {
    patchDefault(id, updates);
  };

  const handleDeleteConfirm = () => {
    if (selectedLocation) {
      deleteDefault(selectedLocation._id);
    }
  };

  if (!hasAccess) {
    return (
      <ThemeProvider theme={Light}>
        <div className={styles.DefaultLocationsContainer}>
          <Heading level={2}>Default Locations</Heading>
          <div className={styles.accessDenied}>
            <span className={styles.WarningIcon}><WarningIcon width={40} height={40} /></span>
            <div className={styles.AccessDeniedMessage}>
              You do not have permission to access this section.
              Only Internal Admins with Full Account Access can manage Default Locations.
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const totalPages = pageSize > 0 ? Math.ceil(total / pageSize) : 0;
  const hasData = !isFetching && locations.length > 0;

  return (
    <ThemeProvider theme={Light}>
      <div className={styles.DefaultLocationsContainer}>
        <div>
          <div className={styles.title}>
            <Heading>Default Locations</Heading>
          </div>
          <div className={styles.actionSection}>
            <div className={styles.platformFilter}>
              <Select
                placeholder="Select platform..."
                options={platformOptions}
                onChange={handlePlatformChange}
                value={platform ? { value: platform, label: platformOptions.find(p => p.value === platform)?.label || platform } : null}
                searchable={false}
                selectStyles={selectStyles}
              />
            </div>
            <div className={styles.addButton}>
              <Button
                buttonStyle="primary"
                onClick={() => showCreateModal()}
              >
                + New Default Location
              </Button>
            </div>
          </div>
        </div>
        {isFetching && (
          <div style={{textAlign: 'center', padding: '40px', position: 'relative', height: '80px'}}>
            <Loading />
          </div>
        )}
        {!isFetching && locations.length === 0 && (
          <div className={styles.noRecords}>No Records to show</div>
        )}
        {hasData && (
          <DefaultLocationsTable
            locations={locations}
            onEdit={(loc) => showEditModal(loc)}
            onDelete={(loc) => showDeleteModal(loc)}
          />
        )}
        <DefaultLocationsTableFooter
          total={total}
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        <CreateDefaultLocationModal
          isOpen={isCreateModalOpen}
          onClose={hideCreateModal}
          onSubmit={handleCreateSubmit}
          onLookupForward={lookupLocationForward}
          onLookupReverse={lookupLocationReverse}
          onClearLookup={clearLookup}
          lookupResults={lookupResults}
          isLookingUp={isLookingUp}
          lookupError={lookupError}
          isCreating={isCreating}
          createError={createError}
          defaultPlatform={platform}
        />
        <EditDefaultLocationModal
          isOpen={isEditModalOpen}
          onClose={hideEditModal}
          onSubmit={handleEditSubmit}
          onLookupForward={lookupLocationForward}
          onLookupReverse={lookupLocationReverse}
          onClearLookup={clearLookup}
          location={selectedLocation}
          lookupResults={lookupResults}
          isLookingUp={isLookingUp}
          lookupError={lookupError}
          isPatching={isPatching}
          patchError={patchError}
        />
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={hideDeleteModal}
          onConfirm={handleDeleteConfirm}
          location={selectedLocation}
          isDeleting={isDeleting}
          deleteError={deleteError}
        />
      </div>
    </ThemeProvider>
  );
};

DefaultLocations.propTypes = {
  styles: PropTypes.object,
  locations: PropTypes.array,
  total: PropTypes.number,
  isFetching: PropTypes.bool,
  error: PropTypes.string,
  platform: PropTypes.string,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  isCreating: PropTypes.bool,
  createError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isPatching: PropTypes.bool,
  patchError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isDeleting: PropTypes.bool,
  deleteError: PropTypes.string,
  isCreateModalOpen: PropTypes.bool,
  isEditModalOpen: PropTypes.bool,
  isDeleteModalOpen: PropTypes.bool,
  selectedLocation: PropTypes.object,
  lookupResults: PropTypes.array,
  isLookingUp: PropTypes.bool,
  lookupError: PropTypes.string,
  isInternalAdmin: PropTypes.bool,
  user: PropTypes.object,
  fetchDefaults: PropTypes.func,
  setPlatformFilter: PropTypes.func,
  setPage: PropTypes.func,
  setPageSize: PropTypes.func,
  createDefault: PropTypes.func,
  patchDefault: PropTypes.func,
  deleteDefault: PropTypes.func,
  lookupLocationForward: PropTypes.func,
  lookupLocationReverse: PropTypes.func,
  clearLookup: PropTypes.func,
  showCreateModal: PropTypes.func,
  hideCreateModal: PropTypes.func,
  showEditModal: PropTypes.func,
  hideEditModal: PropTypes.func,
  showDeleteModal: PropTypes.func,
  hideDeleteModal: PropTypes.func,
  clearState: PropTypes.func,
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(DefaultLocations));
